import React, { useRef } from 'react';
import ReactECharts from 'echarts-for-react';
import * as charts from 'echarts';
import { getFormattedDateArray } from '../../../shared/lib/dateUtils';
import { useTheme } from '../../../contexts/ThemeContext';
import { useMobile } from '../../../contexts/MobileContext';
import useChartAutoResize from '../charting/useChartAutoResize';
import TooltipTrigger from '../../../shared/components/TooltipTrigger';
import {
  getAxisChartGrid,
  colorToRgba,
  getFadedLineStroke,
  getChartSemanticColors,
  getChartTheme,
  getChartTooltipMarker,
  getChartTooltipStyle,
  getChartTypography,
} from '../charting/chartTheme';

const getEmptyState = (text, theme, typography) => ([
  {
    type: 'text',
    left: 'center',
    top: 'middle',
    style: {
      text,
      fill: theme.textMid,
      fontSize: typography.axisNameSize,
      fontWeight: typography.strongWeight,
      textAlign: 'center',
    },
  },
]);

const getEmptyOption = (text, theme, isMobile) => {
  const typography = getChartTypography(isMobile);

  return {
    tooltip: { show: false },
    xAxis: { show: false, type: 'category', data: [] },
    yAxis: { show: false, type: 'value' },
    grid: getAxisChartGrid(isMobile, { hasLegend: false, top: isMobile ? '18%' : '16%' }),
    series: [],
    graphic: getEmptyState(text, theme, typography),
    backgroundColor: 'transparent',
  };
};

const getOption = (site, theme, isMobile) => {
  const colors = getChartSemanticColors(theme);
  const typography = getChartTypography(isMobile);
  const { bots, human, lastDay } = site.lifetimeCounts;
  const status = site?.status || null;
  const totalLifetimeTraffic = human.reduce((sum, value) => sum + value, 0) + bots.reduce((sum, value) => sum + value, 0);
  const waitingForTraffic = status === 'red';
  const hasNoTrafficData = totalLifetimeTraffic === 0;
  const analysingTraffic = !waitingForTraffic && !hasNoTrafficData && human.length === 1 && bots.length === 1;
  const trafficReady = !waitingForTraffic && !hasNoTrafficData && !analysingTraffic;

  if (!trafficReady) {
    return getEmptyOption(
      waitingForTraffic
        ? 'Waiting for traffic...'
        : hasNoTrafficData
          ? 'No traffic data available yet.'
          : 'Not enough traffic history yet.',
      theme,
      isMobile
    );
  }

  const data = human.map((humanValue, index) => {
    const total = humanValue + bots[index];
    return total === 0 ? 0 : bots[index] / total;
  });
  const graphic = [
    {
      type: 'text',
      left: 8,
      top: 4,
      style: {
        text: 'Bot Activity',
        fill: theme.textHigh,
        fontSize: typography.axisNameSize,
        fontWeight: typography.strongWeight,
        padding: [0, 0, isMobile ? 5 : 7, 0],
      },
    },
  ];

  return {
    tooltip: getChartTooltipStyle(theme, typography, {
      show: trafficReady,
      trigger: 'axis',
      axisPointer: {
        type: 'none',
      },
      formatter: (params) => {
        const date = new Date(params[0].name + ' 2024');
        const month = date.toLocaleString('default', { month: 'long' });
        const day = date.getDate();
        const value = Number(params[0].value).toFixed(2);

        return `${month} ${day}<br/>` +
          getChartTooltipMarker(colors.bot) +
          `Fraud: ${value}`;
      },
    }),
    xAxis: {
      show: trafficReady,
      type: 'category',
      boundaryGap: false,
      data: getFormattedDateArray(lastDay, data.length),
      axisLine: {
        lineStyle: {
          color: theme.strokeMid,
          width: 1,
        },
      },
      axisLabel: {
        color: theme.textHigh,
        interval: Math.ceil(bots.length / 4),
        fontSize: typography.axisLabelSize,
      },
    },
    yAxis: {
      show: trafficReady,
      type: 'value',
      min: 0,
      max: 1.2,
      interval: 0.3,
      axisLine: {
        show: false,
      },
      axisLabel: {
        color: theme.textHigh,
        fontSize: typography.axisLabelSize,
        formatter: (value) => String(value).replace(/\.0$/, ''),
      },
      splitLine: {
        lineStyle: {
          color: theme.strokeLow,
          type: 'dashed',
          width: 1,
        },
      },
    },
    grid: getAxisChartGrid(isMobile, { hasLegend: false, top: isMobile ? '18%' : '16%' }),
    series: [
      {
        name: 'Bot Activity',
        type: 'line',
        data,
        smooth: 0.1,
        showSymbol: false,
        symbol: 'circle',
        symbolSize: isMobile ? 7 : 8,
        lineStyle: {
          width: isMobile ? 1.5 : 2,
          color: getFadedLineStroke(charts, colors.bot),
        },
        areaStyle: {
          color: new charts.graphic.LinearGradient(0, 0, 0, 1, [
            { offset: 0, color: colorToRgba(colors.bot, 0.28) },
            { offset: 1, color: colorToRgba(colors.bot, 0.06) },
          ]),
        },
        emphasis: {
          focus: 'series',
          scale: false,
          itemStyle: {
            color: colors.bot,
            borderColor: theme.surfaceLow,
            borderWidth: 2,
          },
        },
      },
    ],
    graphic,
    backgroundColor: 'transparent',
  };
};

const WhenStartedGraph = ({ site, setReady }) => {
  const { isDarkMode } = useTheme();
  const { isMobile } = useMobile();
  const chartRef = useRef(null);
  const theme = React.useMemo(() => getChartTheme(isDarkMode), [isDarkMode]);
  const chartOption = getOption(site, theme, isMobile);
  const isEmptyState = chartOption.series.length === 0;

  useChartAutoResize(chartRef, [site, isDarkMode, isMobile]);

  const handleChartReady = (chart) => {
    chart.off('finished');
    chart.on('finished', () => {
      setReady('when_started');
    });
  };

  React.useEffect(() => {
    if (isEmptyState) {
      setReady('when_started');
    }
  }, [isEmptyState, setReady]);

  return (
    <>
      <div className="when-started-graph">
        <div className="traffic-graph__header">
          <div className="chart-title">Start of Service</div>
          <TooltipTrigger text="This graph shows the rate of fraudulent clicks on your ads since you started using Polygraph." />
        </div>

        <div className="chart-wrap site-overview-graph-shell site-overview-graph-shell--start">
          <ReactECharts
            ref={chartRef}
            option={chartOption}
            opts={{ renderer: isMobile ? 'svg' : 'canvas' }}
            onChartReady={handleChartReady}
            notMerge={true}
            key={`${isDarkMode ? 'dark' : 'light'}-${isMobile ? 'm' : 'd'}-${isEmptyState ? 'empty' : 'data'}`}
            className="chart site-overview-chart-canvas"
          />
        </div>
      </div>
    </>
  );
};

export default WhenStartedGraph;
